import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { GuidelineWithId } from '~/redux/api/types/playbooks/guidelines';
import { Guideline } from '~/redux/api/types/playbooks/playbooks';

import { GuidelineReadOnly } from '../../details/GuidelineReadOnly';
import { GuidelinesDropdown } from '../guidelines-dropdown/GuidelinesDropdown';
import { Guideline as GuidelineComponent } from './Guideline';

interface GuidelineType extends Guideline {
  isReadOnly?: boolean;
}

type Props = {
  onAddNewVariation: () => void;
  onAddExistingVariation: (index: number) => void;
  onChange?: (value: GuidelineType[]) => void;
  guidelines: Array<GuidelineType>;
};

export const Guidelines = ({
  guidelines,
  onAddNewVariation,
  onAddExistingVariation,
  onChange,
}: Props) => {
  const intl = useIntl();

  const handleSelectGuidelines = useCallback(
    (selectedGuidelines: GuidelineWithId[]) => {
      const newGuidelines = selectedGuidelines.map((g) => ({
        ...g,
        id: g.uuid,
        isReadOnly: true,
      }));
      onChange?.([...guidelines, ...newGuidelines]);
    },
    [guidelines, onChange],
  );

  const handleDeleteReadOnlyGuideline = useCallback(
    (guidelineId: string | null | undefined) => {
      onChange?.(
        guidelines.filter((guideline) => guideline.id !== guidelineId),
      );
    },
    [guidelines, onChange],
  );

  const handleGuidelineChange = useCallback(
    (
      guidelineId: string | null | undefined,
      updatedGuidline: Partial<GuidelineType>,
    ) => {
      const updatedGuidelines = guidelines.map((guideline) =>
        guideline.id === guidelineId
          ? { ...guideline, ...updatedGuidline }
          : guideline,
      );
      onChange?.(updatedGuidelines);
    },
    [guidelines, onChange],
  );

  const handleDeleteGuideline = useCallback(
    (guidelineId: string | null | undefined) => {
      onChange?.(
        guidelines.filter((guideline) => guideline.id !== guidelineId),
      );
    },
    [guidelines, onChange],
  );

  const handleDeleteVariation = useCallback(
    (guidelineId: string | null | undefined, variationId: string) => {
      const guideline = guidelines.find((g) => g.id === guidelineId);
      if (!guideline) return;

      const updatedVariations = (guideline.variations ?? []).filter(
        (variation) => variation.clause_variation_id !== variationId,
      );
      const updatedGuidelines = guidelines.map((g) =>
        g.id === guidelineId ? { ...g, variations: updatedVariations } : g,
      );
      onChange?.(updatedGuidelines);
    },
    [guidelines, onChange],
  );

  const handleAddNewGuideline = useCallback(() => {
    onChange?.([
      ...guidelines,
      {
        id: uuidv4(),
        name: '',
        guideline: '',
        sample: '',
        suggest_redlines: true,
      },
    ]);
  }, [guidelines, onChange]);

  const renderGuideline = useCallback(
    (g: GuidelineType, index: number) => {
      if (g.isReadOnly) {
        return (
          <GuidelineReadOnly
            key={g.id ?? ''}
            name={g.name}
            guideline={g.guideline}
            sample={g.sample}
            suggestRedlines={!!g.suggest_redlines}
            variations={g.variations ?? []}
            onDelete={() => handleDeleteReadOnlyGuideline(g.id)}
          />
        );
      }
      return (
        <GuidelineComponent
          variations={g.variations ?? []}
          key={g.id ?? ''}
          value={{
            name: g.name,
            guideline: g.guideline,
            sample: g.sample,
            suggest_redlines: !!g.suggest_redlines,
          }}
          onChange={(updatedGuidline) =>
            handleGuidelineChange(g.id, updatedGuidline)
          }
          onDelete={() => handleDeleteGuideline(g.id)}
          onDeleteVariation={(variationId) =>
            handleDeleteVariation(g.id, variationId)
          }
          onAddNewVariation={onAddNewVariation}
          onAddExistingVariation={() => onAddExistingVariation(index)}
        />
      );
    },
    [
      handleDeleteReadOnlyGuideline,
      handleGuidelineChange,
      handleDeleteGuideline,
      handleDeleteVariation,
      onAddNewVariation,
      onAddExistingVariation,
    ],
  );

  return (
    <Layout direction="column" spacing={4}>
      {guidelines.map((g, index) => renderGuideline(g, index))}
      <Layout flex="1">
        <Box flex="1">
          <GuidelinesDropdown
            selectedIds={guidelines
              .map((guideline) => guideline.id)
              .filter((id): id is string => !!id)}
            onChange={handleSelectGuidelines}
          />
        </Box>
        <Box flex="1" ml={2}>
          <Button
            isFullWidth
            variant="secondary"
            onClick={handleAddNewGuideline}
            text={intl.formatMessage({
              id: 'Guidelines.addGuidelineButton',
              defaultMessage: 'Add New Guideline',
              description: 'Button label to add a new guideline',
            })}
            icon="plus"
            iconPosition="left"
          />
        </Box>
      </Layout>
    </Layout>
  );
};
